import { PermissionCode } from "../domain/identity/access-types.js";

/**
 * MATRIZ ÚNICA DE ACESSO ÀS ÁREAS DO INSPECT IA.
 *
 * Antes deste módulo, o menu (sidebar.ts) e as rotas (main.ts) declaravam
 * permissões separadamente — e já tinham divergido: GESTÃO era gated por
 * `management.access`, que o Inspetor/Técnico não têm, escondendo o RDO do
 * menu mesmo com a rota `/management/rdo` permitindo `rdo.read`. Aqui a
 * área é declarada UMA vez e as duas camadas (menu e rota) leem daqui.
 *
 * Regras:
 * - `permission: null` significa "qualquer usuário autenticado" (Visão Geral).
 * - Acesso a uma ÁREA administrativa nunca é concedido por permissão de
 *   LEITURA DE DADO. Ler Projeto (`project.read`, usado nos formulários de
 *   relatório para escolher o Projeto) não é o mesmo que administrar
 *   Projetos em Gestão → Projetos (`project.manage`). Essa separação é o que
 *   impede o Técnico de alcançar telas administrativas por já possuir a
 *   leitura do dado.
 * - Um grupo de menu aparece quando a sua própria permissão é concedida OU
 *   quando ao menos um item filho é permitido (é assim que o RDO reaparece
 *   para Inspetor/Técnico sem abrir o resto de GESTÃO).
 */

export interface NavigationArea {
  readonly id: string;
  readonly label: string;
  readonly path: string;
  /** null = liberado a qualquer usuário autenticado. */
  readonly permission: string | null;
  readonly children?: readonly NavigationArea[];
  /**
   * Fora do MENU, mas com a rota ainda protegida.
   *
   * Uma área descontinuada não pode simplesmente sair desta matriz: é daqui
   * que `areaByPath` resolve a permissão exigida por URL direta. Removê-la
   * apagaria o gate junto com o item do menu — a rota deixaria de ser negada,
   * só deixaria de ser oferecida. Marcar como oculta mantém a proteção e tira
   * o caminho de entrada duplicado.
   */
  readonly hiddenFromMenu?: boolean;
}

export const NAVIGATION_AREAS: readonly NavigationArea[] = [
  // M16A Etapa 3/8: "/" é agora a Home por Perfil (Painel do Usuário).
  // O antigo Painel Geral moveu-se para /dashboard/indicators com gate RBAC.
  { id: "USER_HOME", label: "INÍCIO", path: "/", permission: null },
  {
    id: "DASHBOARD_INDICATORS",
    label: "Painel de Avanços e Indicadores",
    path: "/dashboard/indicators",
    permission: PermissionCode.DashboardIndicatorsView
  },

  {
    id: "EQUIPMENT", label: "EQUIPAMENTOS & ATIVOS", path: "/equipment", permission: PermissionCode.EquipmentRead,
    children: [
      { id: "EQUIPMENT_LIST", label: "Equipamento / Tubulação", path: "/equipment", permission: PermissionCode.EquipmentRead },
      { id: "EQUIPMENT_NEW", label: "Novo Equipamento", path: "/equipment/new", permission: PermissionCode.EquipmentCreate },
      { id: "EQUIPMENT_IMPORT", label: "Importar Sistemas e Tubulações", path: "/equipment/import", permission: PermissionCode.EquipmentImport },
      { id: "INSTRUMENTS", label: "Instrumentos & Calibração", path: "/instruments", permission: PermissionCode.InstrumentRead },
      { id: "SAFETY_DEVICES", label: "Dispositivos de Segurança (PSV)", path: "/safety-devices", permission: PermissionCode.SafetyDeviceRead },
      { id: "TECHNICAL_ASSETS", label: "Hub de Ativos Técnicos", path: "/technical-assets", permission: PermissionCode.EquipmentRead }
    ]
  },

  {
    id: "INSPECTIONS", label: "INSPEÇÕES", path: "/inspections", permission: PermissionCode.InspectionRead,
    children: [
      { id: "INSPECTIONS_LIST", label: "Inspeções", path: "/inspections", permission: PermissionCode.InspectionRead },
      { id: "INSPECTIONS_NEW", label: "Nova Inspeção", path: "/inspections/new", permission: PermissionCode.InspectionCreate },
      // CONTROLE DE CAMPO: avanço das Linhas em campo. A permissão do item é a
      // do Inspetor (`view_own`) porque é o menor escopo que dá acesso à
      // página — Técnico e Engenheiro também a possuem e enxergam mais abas.
      { id: "FIELD_CONTROL", label: "Controle de Campo", path: "/inspections/field-control", permission: PermissionCode.FieldControlViewOwn }
    ]
  },

  {
    // BLOCO 05: "Relatórios END" (item cujo caminho "/ndt" nunca teve rota
    // registrada — órfão de origem) saiu daqui e virou a Central de
    // Relatórios END, dentro de RELATÓRIOS. B-Scan também não é um item de
    // menu próprio: não existe como relatório listável independente neste
    // sistema (ver comentário em ndt-report-catalog-service.ts).
    id: "NDT", label: "END", path: "/ndt/thickness", permission: PermissionCode.NdtRead,
    children: [
      { id: "NDT_ME", label: "Medição de Espessura", path: "/ndt/thickness", permission: PermissionCode.NdtRead },
      { id: "NDT_LP", label: "Líquido Penetrante (LP)", path: "/ndt/lp", permission: PermissionCode.NdtRead },
      { id: "NDT_PM", label: "Partículas Magnéticas (PM)", path: "/ndt/pm", permission: PermissionCode.NdtRead },
      { id: "NDT_USC", label: "Ultrassom Convencional (US)", path: "/ndt/us-conventional", permission: PermissionCode.NdtRead },
      { id: "NDT_BSCAN", label: "B-Scan / C-Scan", path: "/ndt/bscan", permission: PermissionCode.NdtRead },
      { id: "NDT_REGISTRY", label: "Cadastro de Equipamentos de END", path: "/ndt/equipment", permission: PermissionCode.NdtRegistryRead }
    ]
  },

  {
    // BLOCO 05: "Visualizador de Laudo" removido — apontava para um id fixo
    // fictício (RI-2026-001), placeholder órfão sem uso real (a visualização
    // de laudo de verdade é alcançada a partir do próprio Relatório de
    // Integridade, com o id real). "Relatórios de END" entrou aqui.
    id: "REPORTS", label: "RELATÓRIOS", path: "/reports", permission: PermissionCode.ReportRead,
    children: [
      { id: "REPORTS_INTEGRITY", label: "Relatórios de Integridade", path: "/reports/integrity", permission: PermissionCode.ReportRead },
      { id: "REPORTS_NDT", label: "Relatórios de END", path: "/reports/ndt", permission: PermissionCode.NdtRead }
    ]
  },

  {
    id: "INTEGRITY", label: "INTEGRIDADE", path: "/integrity", permission: PermissionCode.RiRead,
    children: [
      { id: "INTEGRITY_MINUTA", label: "Minuta de Recomendações", path: "/integrity/recommendations-draft", permission: PermissionCode.RecommendationRead },
      { id: "INTEGRITY_RECOMMENDATIONS", label: "Recomendações", path: "/integrity/recommendations", permission: PermissionCode.RecommendationRead },
      { id: "INTEGRITY_AVR", label: "AVR", path: "/integrity/avr", permission: PermissionCode.AvrRead }
    ]
  },

  {
    id: "ENGINEERING", label: "ENGENHARIA", path: "/engineering", permission: PermissionCode.EngineeringAccess,
    children: [
      { id: "ENGINEERING_PANEL", label: "Visão Geral", path: "/engineering", permission: PermissionCode.EngineeringAccess },
      { id: "ENGINEERING_ANALYSIS", label: "Análise", path: "/engineering?tab=analise", permission: PermissionCode.EngineeringAccess },
      { id: "ENGINEERING_REVIEW", label: "Revisão e Aprovação", path: "/engineering?tab=revisao", permission: PermissionCode.EngineeringAccess },
      { id: "ENGINEERING_CORRECTIONS", label: "Correções", path: "/engineering?tab=correcoes", permission: PermissionCode.EngineeringAccess },
      { id: "ENGINEERING_HOLD", label: "Relatórios em HOLD", path: "/engineering?tab=hold", permission: PermissionCode.EngineeringAccess },
      // FINALIZADOS e HISTÓRICO eram uma entrada só ("Aprovados / Histórico").
      // O id antigo é mantido em FINALIZADOS para não perder o gate que
      // `areaByPath` usa — remover a entrada removeria também a negação da URL
      // direta (CLAUDE.md, seção 30).
      { id: "ENGINEERING_APPROVED", label: "Finalizados", path: "/engineering?tab=finalizados", permission: PermissionCode.EngineeringAccess },
      { id: "ENGINEERING_HISTORY", label: "Histórico", path: "/engineering?tab=historico", permission: PermissionCode.EngineeringAccess },
      { id: "ENGINEERING_CALC", label: "Cálculos", path: "/engineering/calculations", permission: PermissionCode.CalculationAccess }
    ]
  },

  {
    // GESTÃO: o grupo exige management.access, mas o RDO tem permissão
    // própria — por isso o grupo aparece para quem só tem RDO (regra do
    // "grupo visível quando algum filho é permitido").
    id: "MANAGEMENT", label: "GESTÃO", path: "/management", permission: PermissionCode.ManagementAccess,
    children: [
      { id: "MANAGEMENT_CLIENTS", label: "Clientes", path: "/management/clients", permission: PermissionCode.ClientManage },
      { id: "MANAGEMENT_PROJECTS", label: "Projetos", path: "/management/projects", permission: PermissionCode.ProjectManage },
      { id: "MANAGEMENT_OS", label: "Ordem de Serviço (OS)", path: "/management/os", permission: PermissionCode.ManagementAccess },
      { id: "MANAGEMENT_FRONTS", label: "Frentes de Serviço", path: "/management/fronts", permission: PermissionCode.ManagementAccess },
      { id: "MANAGEMENT_RDO", label: "RDO", path: "/management/rdo", permission: PermissionCode.RdoRead }
    ]
  },

  {
    id: "DOCUMENTATION", label: "DOCUMENTAÇÃO", path: "/documentation", permission: PermissionCode.DocumentRead,
    children: [
      { id: "DOCUMENTATION_LIBRARY", label: "Biblioteca", path: "/documentation/library", permission: PermissionCode.DocumentRead },
      { id: "DOCUMENTATION_ATTACH", label: "Anexar Documento", path: "/documentation/attach", permission: PermissionCode.DocumentWrite },
      { id: "DOCUMENTATION_BOOK", label: "Book de Inspeção", path: "/documentation/book", permission: PermissionCode.EquipmentRead }
    ]
  },

  {
    id: "ADMINISTRATION", label: "ADMINISTRAÇÃO", path: "/administration", permission: PermissionCode.AdministrationRead,
    children: [
      // PESSOAS E ACESSOS é a única entrada operacional de pessoas. As três
      // listagens anteriores mostravam a MESMA pessoa três vezes; saíram do
      // MENU para que não exista mais de um lugar oficial para cadastrá-la,
      // mas continuam aqui como `hiddenFromMenu` — a rota segue exigindo a
      // mesma permissão para quem chegar por URL direta ou link salvo.
      { id: "ADMINISTRATION_PEOPLE", label: "Pessoas e Acessos", path: "/administration/people", permission: PermissionCode.IdentityRead },
      { id: "ADMINISTRATION_BACKUP", label: "Backup e Restauração", path: "/administration/backup", permission: PermissionCode.BackupRead },
      { id: "ADMINISTRATION_USERS", label: "Usuários", path: "/administration/users", permission: PermissionCode.UserRead, hiddenFromMenu: true },
      { id: "ADMINISTRATION_PROFESSIONALS", label: "Profissionais", path: "/administration/professionals", permission: PermissionCode.IdentityManage, hiddenFromMenu: true },
      { id: "ADMINISTRATION_SIGNATORIES", label: "Signatários", path: "/administration/signatories", permission: PermissionCode.IdentityManage, hiddenFromMenu: true }
    ]
  },

  // CONFIGURAÇÕES — parametrização funcional (comportamento, regras, segurança,
  // integrações). Aparência é do STUDIO. Permissão oficial: settings.read.
  { id: "SETTINGS", label: "CONFIGURAÇÕES", path: "/settings", permission: PermissionCode.SettingsRead },

  // STUDIO INSPECT IA — identidade e aparência (como o sistema APARECE).
  // Os sete itens abaixo são o shell de extensões low-code, que nunca teve
  // tela funcional (todos caíam no placeholder "disponibilizado em etapa
  // posterior"). Saíram do MENU, mas continuam aqui como `hiddenFromMenu`
  // para a rota seguir exigindo a mesma permissão; o código low-code
  // (domínio, serviço, tabelas) não foi tocado.
  {
    id: "STUDIO", label: "STUDIO INSPECT IA", path: "/studio", permission: PermissionCode.StudioRead,
    children: [
      { id: "STUDIO_FORMS", label: "Formulários", path: "/studio/forms", permission: PermissionCode.StudioCreate, hiddenFromMenu: true },
      { id: "STUDIO_CHECKLISTS", label: "Checklists", path: "/studio/checklists", permission: PermissionCode.StudioCreate, hiddenFromMenu: true },
      { id: "STUDIO_CATALOGS", label: "Catálogos", path: "/studio/catalogs", permission: PermissionCode.StudioCreate, hiddenFromMenu: true },
      { id: "STUDIO_RULES", label: "Regras", path: "/studio/rules", permission: PermissionCode.StudioCreate, hiddenFromMenu: true },
      { id: "STUDIO_REPORTS", label: "Relatórios", path: "/studio/reports", permission: PermissionCode.StudioCreate, hiddenFromMenu: true },
      { id: "STUDIO_FORMULAS", label: "Fórmulas", path: "/studio/formulas", permission: PermissionCode.StudioCreate, hiddenFromMenu: true },
      { id: "STUDIO_WORKFLOWS", label: "Fluxos", path: "/studio/workflows", permission: PermissionCode.StudioCreate, hiddenFromMenu: true }
    ]
  },

  // CÓRTEX descontinuado (2026-09-27): a IA oficial é o INSPECT IA REVIEW. A
  // rota antiga /cortex redireciona para cá.
  { id: "AI_REVIEW", label: "INSPECT IA REVIEW", path: "/ai-review", permission: PermissionCode.AiReviewRead }
];

/** Checagem de permissão fornecida pela camada chamadora (contextStore no browser, RequestContext no servidor). */
export type PermissionChecker = (permission: string) => boolean;

/** Acesso direto à área (sem considerar filhos) — é o que a ROTA exige. */
export function canAccessArea(has: PermissionChecker, area: NavigationArea): boolean {
  return area.permission === null || has(area.permission);
}

/**
 * Visibilidade do grupo no MENU: a própria área ou qualquer filho permitido.
 * É o que faz o RDO aparecer para Inspetor/Técnico sem abrir Clientes,
 * Projetos, OS ou Frentes.
 */
export function isAreaVisible(has: PermissionChecker, area: NavigationArea): boolean {
  if (area.hiddenFromMenu) return false;
  if (canAccessArea(has, area)) return true;
  return (area.children || []).some(child => !child.hiddenFromMenu && canAccessArea(has, child));
}

/** Filhos que o usuário realmente pode abrir — o menu nunca mostra um item que a rota negaria. */
export function visibleChildren(has: PermissionChecker, area: NavigationArea): NavigationArea[] {
  return (area.children || []).filter(child => !child.hiddenFromMenu && canAccessArea(has, child));
}

/** Áreas de topo visíveis, na ordem oficial — usado pela Sidebar (desktop e Drawer mobile, o mesmo cálculo). */
export function visibleAreas(has: PermissionChecker): NavigationArea[] {
  return NAVIGATION_AREAS.filter(area => isAreaVisible(has, area));
}

/** Busca a área (topo ou filho) que corresponde a um caminho — usado nos testes de consistência menu × rota. */
export function areaByPath(path: string): NavigationArea | null {
  for (const area of NAVIGATION_AREAS) {
    if (area.path === path) return area;
    for (const child of area.children || []) {
      if (child.path === path) return child;
    }
  }
  return null;
}
